import {
  Button,
  Menu,
  Modal,
  Text,
  ThemeIcon,
  Stack,
  Group,
  Divider,
  Textarea,
} from "@mantine/core";
import { useState } from "react";
import { toast } from "sonner";
import { intentionStatusCardOrder, intentionStatusData } from "../utils/consts";
import { useUpdatePaymentIntentionStatusMutation } from "../api/queries";
import { useAuth } from "../hooks/useAuth";
import { displayIntentionStatus } from "../utils/display";
import { IntentionStatus } from "../utils/types";

interface InvoiceStatusMenuProps {
  currentStatus: IntentionStatus;
  externalId: string;
}

export const InvoiceStatusMenu = ({
  currentStatus,
  externalId,
}: InvoiceStatusMenuProps) => {
  const { canMutateOrg } = useAuth();
  const updateIntentionStatusMutation =
    useUpdatePaymentIntentionStatusMutation();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingNewStatus, setPendingNewStatus] =
    useState<IntentionStatus | null>(null);
  const [description, setDescription] = useState<string>("");

  const Icon = intentionStatusData[currentStatus].icon;

  const handleStatusClick = (newStatus: IntentionStatus) => {
    if (newStatus !== currentStatus) {
      setPendingNewStatus(newStatus);
      setConfirmDialogOpen(true);
    }
  };

  const handleConfirm = async () => {
    if (pendingNewStatus) {
      try {
        await updateIntentionStatusMutation.mutateAsync({
          externalId,
          intentionStatus: pendingNewStatus,
          description: description.trim() || undefined,
        });
        setConfirmDialogOpen(false);
        setPendingNewStatus(null);
        setDescription("");
      } catch {
        toast.error("Failed to update collection status");
      }
    }
  };

  const handleClose = () => {
    setConfirmDialogOpen(false);
    setPendingNewStatus(null);
    setDescription("");
  };

  if (!canMutateOrg) {
    return (
      <Button
        variant="subtle"
        radius="md"
        color={intentionStatusData[currentStatus].tableIconColor}
        bg={`${intentionStatusData[currentStatus].tableIconColor}.0`}
        disabled
      >
        <ThemeIcon
          color={intentionStatusData[currentStatus].tableIconColor}
          variant="light"
          radius="xl"
          size="md"
        >
          <Icon size={20} />
        </ThemeIcon>
      </Button>
    );
  }

  return (
    <>
      <Menu
        position="bottom-start"
        withinPortal
        transitionProps={{ transition: "pop", duration: 200 }}
        shadow="lg"
        radius="md"
      >
        <Menu.Target>
          <Button
            variant="subtle"
            radius="md"
            color={intentionStatusData[currentStatus].tableIconColor}
            onClick={(e) => e.stopPropagation()}
            bg={`${intentionStatusData[currentStatus].tableIconColor}.0`}
            className="transition-all duration-200 hover:scale-105 hover:shadow-md"
            styles={{
              root: {
                "&:hover": {
                  backgroundColor: `var(--mantine-color-${intentionStatusData[currentStatus].tableIconColor}-1)`,
                  transform: "scale(1.05)",
                },
              },
            }}
          >
            <ThemeIcon
              color={intentionStatusData[currentStatus].tableIconColor}
              variant="light"
              radius="xl"
              size="md"
              className="transition-all duration-200 hover:rotate-6"
            >
              <Icon size={20} />
            </ThemeIcon>
          </Button>
        </Menu.Target>

        <Menu.Dropdown
          className="py-2"
          style={{
            boxShadow:
              "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div className="px-3 py-2">
            <Text
              size="xs"
              fw={700}
              c="dimmed"
              tt="uppercase"
              style={{ letterSpacing: "0.08em" }}
            >
              Collection status
            </Text>
          </div>
          <Divider my={4} />

          {intentionStatusCardOrder.map((status) => {
            const StatusIcon = intentionStatusData[status].icon;
            const isSelected = status === currentStatus;
            const statusLabel = displayIntentionStatus(status) ?? status;

            return (
              <Menu.Item
                key={status}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isSelected) {
                    handleStatusClick(status);
                  }
                }}
                data-selected={isSelected}
                styles={{
                  item: {
                    padding: "10px 16px",
                    margin: "2px 8px",
                    borderRadius: "6px",
                    backgroundColor: isSelected
                      ? "var(--mantine-color-blue-0) !important"
                      : "transparent",
                    transition: "background-color 0.15s ease-in-out",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? "var(--mantine-color-blue-1) !important"
                        : "var(--mantine-color-gray-1) !important",
                    },
                  },
                }}
                className={`transition-all duration-150 ${
                  isSelected ? "cursor-not-allowed" : "cursor-pointer"
                } ${isSelected ? "hover:bg-blue-100!" : "hover:bg-gray-100!"}`}
                leftSection={
                  <ThemeIcon
                    color={intentionStatusData[status].tableIconColor}
                    variant="light"
                    size="md"
                    radius="xl"
                    className={isSelected ? "opacity-60" : ""}
                  >
                    <StatusIcon size={16} />
                  </ThemeIcon>
                }
              >
                <Text
                  size="sm"
                  fw={isSelected ? 600 : 500}
                  c={isSelected ? "blue" : "gray.8"}
                  className={isSelected ? "font-semibold" : ""}
                >
                  {statusLabel}
                </Text>
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>

      {/* Confirm Modal */}
      {confirmDialogOpen && pendingNewStatus && (
        <Modal
          opened
          onClose={handleClose}
          centered
          title={
            <Group gap="xs">
              <ThemeIcon
                color={intentionStatusData[pendingNewStatus].tableIconColor}
                variant="light"
                size="lg"
                radius="xl"
              >
                {(() => {
                  const PendingIcon =
                    intentionStatusData[pendingNewStatus].icon;
                  return <PendingIcon size={20} />;
                })()}
              </ThemeIcon>
              <Text size="xl" fw={700}>
                Confirm Status Change
              </Text>
            </Group>
          }
          radius="md"
          padding="xl"
          styles={{
            content: {
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            },
            header: {
              borderBottom: "1px solid #e5e7eb",
              paddingBottom: "16px",
              marginBottom: "8px",
            },
          }}
        >
          <Stack gap="lg">
            <Text size="sm" c="dimmed" fw={500}>
              Are you sure you want to change the status of this invoice?
            </Text>

            <div className="bg-linear-to-br from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200 shadow-inner">
              <Group justify="space-between" mb="md">
                <Text
                  size="sm"
                  fw={600}
                  c="dimmed"
                  tt="uppercase"
                  style={{ letterSpacing: "0.05em" }}
                >
                  Invoice
                </Text>
                <Text size="sm" fw={700} className="text-gray-900">
                  {externalId}
                </Text>
              </Group>

              <Group justify="space-between" mb="xs">
                <Text
                  size="sm"
                  fw={600}
                  c="dimmed"
                  tt="uppercase"
                  style={{ letterSpacing: "0.05em" }}
                >
                  From
                </Text>
                <Group gap="xs">
                  <ThemeIcon
                    color={intentionStatusData[currentStatus].tableIconColor}
                    variant="light"
                    size="sm"
                    radius="xl"
                  >
                    {(() => {
                      const CurrentIcon =
                        intentionStatusData[currentStatus].icon;
                      return <CurrentIcon size={14} />;
                    })()}
                  </ThemeIcon>
                  <Text
                    size="sm"
                    fw={700}
                    c={intentionStatusData[currentStatus].tableIconColor}
                  >
                    {displayIntentionStatus(currentStatus) ?? currentStatus}
                  </Text>
                </Group>
              </Group>

              <Group
                justify="space-between"
                mt="md"
                pt="md"
                style={{ borderTop: "1px solid #e5e7eb" }}
              >
                <Text
                  size="sm"
                  fw={600}
                  c="dimmed"
                  tt="uppercase"
                  style={{ letterSpacing: "0.05em" }}
                >
                  To
                </Text>
                <Group gap="xs">
                  <ThemeIcon
                    color={intentionStatusData[pendingNewStatus].tableIconColor}
                    variant="light"
                    size="sm"
                    radius="xl"
                  >
                    {(() => {
                      const PendingIcon =
                        intentionStatusData[pendingNewStatus].icon;
                      return <PendingIcon size={14} />;
                    })()}
                  </ThemeIcon>
                  <Text
                    size="sm"
                    fw={700}
                    c={intentionStatusData[pendingNewStatus].tableIconColor}
                  >
                    {displayIntentionStatus(pendingNewStatus) ??
                      pendingNewStatus}
                  </Text>
                </Group>
              </Group>
            </div>

            <Textarea
              label={
                <Text size="sm" fw={600} mb={4}>
                  Description (optional)
                </Text>
              }
              placeholder="Add a note about this status change..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              radius="md"
              styles={{
                input: {
                  fontSize: "14px",
                  lineHeight: "1.6",
                },
              }}
            />

            <Group justify="flex-end" mt="md" gap="sm">
              <Button
                variant="subtle"
                color="gray"
                radius="md"
                onClick={handleClose}
                className="transition-all duration-150"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                color="blue"
                radius="md"
                loading={updateIntentionStatusMutation.isPending}
                className="transition-all duration-150 hover:scale-105 hover:shadow-md"
              >
                Confirm Change
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </>
  );
};
